import { AdvancedDynamicTexture, TextBlock, Rectangle } from '@babylonjs/gui';
import { MeshAssembly } from '../../MeshAssembly';
import {
	TextConfiguration,
	TextUiConfiguration,
	DivUiConfiguration,
} from '../../configuration/interfaces/TextConfiguration';
import MeshFactory from '../MeshFactory';

export default class TextBuilder {
	private advancedTexture: AdvancedDynamicTexture;
	constructor(private meshFactory: MeshFactory) {
		this.advancedTexture = AdvancedDynamicTexture.CreateFullscreenUI('UI');
	}
	/**
	 * Create Text.
	 */
	public createText(configuration: TextConfiguration, id: string): MeshAssembly {
		const { textConfig, divConfig } = this.parseTextConfiguration(configuration);
		const assembly = this.meshFactory.createBox({ width: 1, height: 1, depth: 1 }, id);
		const name = String(assembly.uniqueId);
		const uiText = new TextBlock(name);
		const uiDiv = new Rectangle(name);
		uiText.resizeToFit = true;
		uiDiv.adaptWidthToChildren = true;
		uiDiv.adaptHeightToChildren = true;
		uiDiv.isVisible = false;

		// Configire Div
		Object.entries(divConfig).forEach(([i, v]) => (v || v == 0 ? (uiDiv[i] = v) : null));
		this.advancedTexture.addControl(uiDiv);

		// Configure Text
		Object.entries(textConfig).forEach(([i, v]) => (v || v == 0 ? (uiText[i] = v) : null));
		uiText.isVisible = true; // TODO implement
		uiDiv.addControl(uiText);
		uiDiv.linkWithMesh(assembly.transformNode.getChildMeshes()[0]);

		return assembly;
	}

	/**
	 * Modify Text.
	 */
	public modifyText(assembly: MeshAssembly, configuration: TextConfiguration): void {
		const name = String(assembly.uniqueId);
		const uiDiv = this.advancedTexture.rootContainer.getChildByName(name) as Rectangle;
		const uiText = uiDiv.getChildByName(name) as TextBlock;
		const { textConfig, divConfig } = this.parseTextConfiguration(configuration);
		// Configire Div
		Object.entries(divConfig).forEach(([i, v]) => (v || v == 0 ? (uiDiv[i] = v) : null));
		// Configure Text
		Object.entries(textConfig).forEach(([i, v]) => (v || v == 0 ? (uiText[i] = v) : null));
	}

	/**
	 * Destroy Text.
	 */
	public destroyText(assembly: MeshAssembly): void {
		const name = String(assembly.uniqueId);
		const childName = this.advancedTexture.rootContainer.getChildByName(name);
		if (childName) {
			childName.dispose();
		}
	}

	/**
	 * Render/Not render Text.
	 */
	public shouldRenderText(assembly: MeshAssembly, value: boolean): void {
		const name = String(assembly.uniqueId);
		const childName = this.advancedTexture.rootContainer.getChildByName(name);
		if (childName) {
			childName.isVisible = value;
		}
	}

	private parseTextConfiguration(configuration: TextConfiguration): {
		textConfig: TextUiConfiguration;
		divConfig: DivUiConfiguration;
	} {
		const {
			text,
			color,
			fontSize,
			fontFamily,
			fontWeight,
			fontStyle,
			border,
			backgroundColor,
			borderColor,
			borderRadius,
			padding,
			alignment,
		}: TextConfiguration = configuration;

		let textHorizontalAlignment: number;
		switch (alignment) {
			case 'left':
				textHorizontalAlignment = 0;
				break;
			case 'right':
				textHorizontalAlignment = 1;
				break;
			case 'center':
				textHorizontalAlignment = 2;
				break;
			default:
				textHorizontalAlignment = 0;
				break;
		}
		const textConfig: TextUiConfiguration = {
			text,
			color,
			fontSize,
			fontFamily,
			fontWeight,
			fontStyle,
			textHorizontalAlignment,
		};

		const divConfig: DivUiConfiguration = {
			thickness: border || 0,
			cornerRadius: borderRadius as number,
			background: backgroundColor as string,
			color: borderColor as string,
			paddingBottom: Array.isArray(padding) ? -padding[0] : -(padding as number),
			paddingRight: Array.isArray(padding) ? -padding[1] : -(padding as number),
		};

		return {
			textConfig,
			divConfig,
		};
	}
}
